import { useCallback, useRef, useState } from 'react';
import { useEditorRef } from 'platejs/react';
import { useAppSelector } from '../../../app/hooks';
import { streamAskAicaChat } from '../../../store/dashboard/askAica/apiHelper';
import { extractEditorContext } from './serializeSelection';
import { markdownToPlate } from './markdownToPlate';

export interface AskAicaMessage {
    role: 'user' | 'assistant';
    content: string;
}

export interface UseAIMenuArgs {
    /**
     * Subject company org id for the askAica RAG agent. For the One Pager this
     * is the deal's `assesseeOrgId`. When absent we fall back to the investor
     * org (and, as a last resort, the localStorage value inside CHAT_CONFIG).
     */
    orgId?: string;
}

export interface UseAIMenuReturn {
    isOpen: boolean;
    open: () => void;
    close: () => void;
    toggle: () => void;
    prompt: string;
    setPrompt: (v: string) => void;
    streaming: boolean;
    error: string | null;
    /** Streamed answer (markdown) held in the preview — NOT yet in the document. */
    result: string;
    /** True when non-empty text was selected at submit time (Replace vs Insert). */
    hadSelection: boolean;
    /** Snapshot of editor.selection captured at menu-open time. The browser
     *  paints selection on the focused element only, so when the prompt input
     *  grabs focus the editor's blue highlight disappears — the GhostSelectionOverlay
     *  re-paints a static highlight from this saved range so the user can
     *  still see what they're editing. */
    savedSelection: any | null;
    submit: () => void;
    /** Commit the preview: replace the selection if there was one, else insert below. */
    accept: () => void;
    /** Commit the preview by inserting below the cursor regardless of selection. */
    insertBelow: () => void;
    /** Discard the preview without touching the document. */
    discard: () => void;
    /** Re-run the same prompt. */
    retry: () => void;
    abort: () => void;
}

const isCollapsedSelection = (sel: any): boolean =>
    !sel ||
    !sel.anchor ||
    !sel.focus ||
    (sel.anchor.offset === sel.focus.offset && JSON.stringify(sel.anchor.path) === JSON.stringify(sel.focus.path));

export function useAIMenu({ orgId }: UseAIMenuArgs = {}): UseAIMenuReturn {
    const editor = useEditorRef();
    const userData = useAppSelector((state: any) => state.user?.userData);
    const userId: string = userData?.userId ?? '';
    const investorOrgId: string = userData?.investorOrgId ?? '';

    const [isOpen, setIsOpen] = useState(false);
    const [prompt, setPrompt] = useState('');
    const [streaming, setStreaming] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // The streamed answer lives here as markdown and is shown in the popover
    // preview. NOTHING is written to the document until the user accepts.
    const [result, setResult] = useState('');
    const [hadSelection, setHadSelection] = useState(false);
    // Mirror of savedSelectionRef as state — exposed in the hook return so
    // the GhostSelectionOverlay can re-render its highlight rects when the
    // captured selection changes (open/close transitions).
    const [savedSelection, setSavedSelection] = useState<any | null>(null);

    // Cleanup fn returned by streamAskAicaChat — calling it aborts the XHR.
    const abortRef = useRef<(() => void) | null>(null);
    // Set when the user cancels — lets us swallow the status-0 onError that
    // XHR.abort() triggers so a deliberate Stop doesn't surface as an error.
    const userAbortRef = useRef(false);
    // Snapshot of editor.selection captured the moment the menu OPENS. The
    // input steals focus immediately, clearing editor.selection — we restore
    // from this snapshot to extract the selected text and to commit later.
    const savedSelectionRef = useRef<any>(null);

    const stopStream = useCallback(() => {
        if (abortRef.current) {
            userAbortRef.current = true;
            abortRef.current();
            abortRef.current = null;
        }
    }, []);

    const resetState = useCallback(() => {
        setPrompt('');
        setError(null);
        setResult('');
        setHadSelection(false);
        savedSelectionRef.current = null;
        setSavedSelection(null);
    }, []);

    const captureSelection = useCallback(() => {
        const ed: any = editor;
        // Deep clone — Slate selection objects are not safe to hold across
        // editor mutations. JSON round-trip is fine (pure-value tree).
        const snap = ed?.selection ? JSON.parse(JSON.stringify(ed.selection)) : null;
        savedSelectionRef.current = snap;
        setSavedSelection(snap);
    }, [editor]);

    const open = useCallback(() => {
        setError(null);
        setResult('');
        captureSelection();
        setIsOpen(true);
    }, [captureSelection]);

    const close = useCallback(() => {
        setIsOpen(false);
        stopStream();
        resetState();
    }, [stopStream, resetState]);

    const toggle = useCallback(() => {
        // Capture BEFORE flipping state — once the menu mounts and its input
        // grabs focus, editor.selection is already null.
        setIsOpen((prev) => {
            if (prev) {
                stopStream();
                resetState();
            } else {
                setError(null);
                setResult('');
                captureSelection();
            }
            return !prev;
        });
    }, [captureSelection, stopStream, resetState]);

    const abort = useCallback(() => {
        stopStream();
        setStreaming(false);
    }, [stopStream]);

    const runStream = useCallback(() => {
        const ed: any = editor;
        const savedSel = savedSelectionRef.current;
        if (savedSel) {
            try {
                ed.tf?.select?.(savedSel);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('[Plate AI] selection restore failed', err);
            }
        }

        const ctx = extractEditorContext(editor);
        const selecting = !!(ctx.selectedText && savedSel && !isCollapsedSelection(savedSel));
        setHadSelection(selecting);

        const userParts: string[] = [];
        if (selecting) {
            // Selection-edit mode: ask AICA to revise the selected text and
            // return ONLY the revised text, so Replace swaps it cleanly.
            userParts.push(`Revise the following text and return ONLY the revised version, with no preamble:\n${ctx.selectedText}\n`);
            if (ctx.surroundingContext) userParts.push(`Document context:\n${ctx.surroundingContext}\n`);
            userParts.push(`Instruction: ${prompt.trim()}`);
        } else {
            if (ctx.surroundingContext) userParts.push(`Document context:\n${ctx.surroundingContext}\n`);
            userParts.push(`Request: ${prompt.trim()}`);
        }
        const userMessage: AskAicaMessage = { role: 'user', content: userParts.join('\n') };

        // Single-shot: the request carries only the current prompt. No
        // multi-turn history is kept or sent.
        const thread: AskAicaMessage[] = [userMessage];

        const org_id = orgId || investorOrgId;

        userAbortRef.current = false;
        setStreaming(true);
        setError(null);
        setResult('');

        const abortFn = streamAskAicaChat(
            thread,
            [], // sources: web search OFF for the in-editor assistant
            userId,
            org_id,
            (content, _suggestedQuestions, _reasoning, _hasNewReasoning, _isFirst, metadata) => {
                if (metadata?.error) {
                    // eslint-disable-next-line no-console
                    console.error('[Plate AI] askAica reported error:', metadata.errorMessage);
                    setError(metadata.errorMessage || 'AICA returned an error');
                    return;
                }
                if (!content) return; // reasoning-only frame — nothing to preview yet
                setResult(content);
            },
            () => {
                setStreaming(false);
                abortRef.current = null;
            },
            (err: any) => {
                abortRef.current = null;
                setStreaming(false);
                if (userAbortRef.current) {
                    userAbortRef.current = false;
                    return;
                }
                // eslint-disable-next-line no-console
                console.error('[Plate AI] stream error:', err);
                setError(err?.message ?? 'AI request failed');
            },
        );
        abortRef.current = abortFn;
    }, [editor, prompt, orgId, userId, investorOrgId]);

    const submit = useCallback(() => {
        if (!prompt.trim() || streaming) return;
        runStream();
    }, [prompt, streaming, runStream]);

    const retry = useCallback(() => {
        if (streaming || !prompt.trim()) return;
        runStream();
    }, [streaming, prompt, runStream]);

    const commit = useCallback(
        (mode: 'replace' | 'insertBelow') => {
            const ed: any = editor;
            const nodes = markdownToPlate(ed, result) as any[];
            if (!nodes || nodes.length === 0) {
                close();
                return;
            }
            const savedSel = savedSelectionRef.current;
            try {
                if (mode === 'replace' && savedSel && !isCollapsedSelection(savedSel)) {
                    ed.tf?.select?.(savedSel);
                    ed.tf?.delete?.();
                    ed.tf?.insertFragment?.(nodes);
                } else {
                    // Insert below the block the cursor/selection started in.
                    const blockIndex: number = (savedSel?.anchor?.path?.[0] as number | undefined) ?? (ed.children?.length ?? 1) - 1;
                    const at = blockIndex + 1;
                    nodes.forEach((node: any, i: number) => {
                        try {
                            ed.tf.insertNodes(node, { at: [at + i] });
                        } catch (err) {
                            // eslint-disable-next-line no-console
                            console.error('[Plate AI] insertNodes failed at', [at + i], err);
                        }
                    });
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('[Plate AI] commit failed:', err);
            }
            close();
        },
        [editor, result, close],
    );

    const accept = useCallback(() => commit(hadSelection ? 'replace' : 'insertBelow'), [commit, hadSelection]);
    const insertBelow = useCallback(() => commit('insertBelow'), [commit]);
    const discard = useCallback(() => close(), [close]);

    return {
        isOpen,
        open,
        close,
        toggle,
        prompt,
        setPrompt,
        streaming,
        error,
        result,
        hadSelection,
        savedSelection,
        submit,
        accept,
        insertBelow,
        discard,
        retry,
        abort,
    };
}
